import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Printer, ArrowLeft, IndianRupee, Ban, RotateCcw, Receipt as ReceiptIcon } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { inr, fmtDate } from "@/lib/format";
import { todayISO } from "@/lib/period";
import { useAuth } from "@/context/AuthContext";
import Modal from "@/components/Modal";

function TaxBreak({ inv }) {
  // group by tax rate
  const groups = {};
  inv.lines.forEach((l) => {
    if (l.tax_rate == null) return;
    groups[l.tax_rate] = groups[l.tax_rate] || { taxable: 0, tax: 0 };
    groups[l.tax_rate].taxable += l.taxable ?? (l.qty * l.rate - l.discount);
    groups[l.tax_rate].tax += l.tax_amount ?? 0;
  });
  const split = inv.tax_mode === "split";
  return (
    <table className="w-full text-xs border-collapse mb-4" style={{ border: "1px solid #E6E1D8" }}>
      <thead>
        <tr style={{ background: "#F4F0EA" }}>
          <th className="text-left p-2" style={{ border: "1px solid #E6E1D8" }}>Taxable Value</th>
          {split ? (<>
            <th className="text-right p-2" style={{ border: "1px solid #E6E1D8" }}>CGST</th>
            <th className="text-right p-2" style={{ border: "1px solid #E6E1D8" }}>SGST</th>
          </>) : <th className="text-right p-2" style={{ border: "1px solid #E6E1D8" }}>GST</th>}
          <th className="text-right p-2" style={{ border: "1px solid #E6E1D8" }}>Total Tax</th>
        </tr>
      </thead>
      <tbody>
        {Object.entries(groups).map(([rate, g]) => (
          <tr key={rate}>
            <td className="p-2 font-num" style={{ border: "1px solid #E6E1D8" }}>{inr(g.taxable)} <span className="text-[#8C929B]">@ {rate}%</span></td>
            {split ? (<>
              <td className="p-2 text-right font-num" style={{ border: "1px solid #E6E1D8" }}>{inr(g.tax / 2)} <span className="text-[#8C929B]">({(rate / 2)}%)</span></td>
              <td className="p-2 text-right font-num" style={{ border: "1px solid #E6E1D8" }}>{inr(g.tax / 2)} <span className="text-[#8C929B]">({(rate / 2)}%)</span></td>
            </>) : <td className="p-2 text-right font-num" style={{ border: "1px solid #E6E1D8" }}>{inr(g.tax)} ({rate}%)</td>}
            <td className="p-2 text-right font-num" style={{ border: "1px solid #E6E1D8" }}>{inr(g.tax)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function A4Invoice({ inv, settings }) {
  const stampColor = inv.status === "Paid" ? "#2D5A3C" : inv.balance > 0 ? "#A3411D" : "#8C929B";
  const stampText = inv.status === "Paid" ? "PAID" : inv.status === "Partial" ? "PARTIAL" : inv.status === "Void" ? "VOID" : inv.status === "Returned" ? "RETURNED" : "DUE";
  return (
    <div className="invoice-sheet card">
      <div className="flex justify-between items-start pb-5 border-b-2" style={{ borderColor: "#0E1B2E" }}>
        <div className="flex gap-3">
          <div className="w-14 h-14 shrink-0 rounded-full bg-[#0E1B2E] text-white grid place-items-center font-display text-xl">{(settings.company_name || "").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("")}</div>
          <div>
            <div className="font-display text-2xl">{settings.company_name}</div>
            {settings.legal_name && <div className="text-xs text-[#5C626A] mt-1 max-w-xs">{settings.legal_name}</div>}
            {settings.address && <div className="text-xs text-[#5C626A] max-w-xs">{settings.address}</div>}
            {(settings.phone || settings.email) && <div className="text-xs text-[#5C626A]">{[settings.phone, settings.email].filter(Boolean).join(" · ")}</div>}
            {settings.gstin && <div className="text-xs font-num mt-1"><b>GSTIN:</b> {settings.gstin}{settings.state ? ` · ${settings.state}` : ""}</div>}
          </div>
        </div>
        <div className="text-right">
          <div className="font-display text-3xl tracking-wide">TAX INVOICE</div>
          <div className="text-sm font-num mt-2">{inv.invoice_number}</div>
          <div className="text-xs text-[#5C626A]">Date: {fmtDate(inv.date)}</div>
          {inv.due_date && <div className="text-xs text-[#5C626A]">Due: {fmtDate(inv.due_date)}</div>}
        </div>
      </div>

      <div className="flex justify-between py-4">
        <div>
          <div className="eyebrow mb-1">Bill To</div>
          <div className="font-semibold">{inv.customer_snapshot?.name || inv.customer_name}</div>
          {inv.customer_snapshot?.phone && <div className="text-xs text-[#5C626A]">{inv.customer_snapshot.phone}</div>}
          {inv.customer_snapshot?.address && <div className="text-xs text-[#5C626A] max-w-xs">{inv.customer_snapshot.address}</div>}
          {inv.customer_snapshot?.tax_id && <div className="text-xs font-num">GSTIN: {inv.customer_snapshot.tax_id}</div>}
        </div>
        <div className="text-right">
          <div className="eyebrow mb-1">Channel</div>
          <div className="text-sm">{inv.channel}</div>
          <div className="mt-3"><span className="stamp" style={{ borderColor: stampColor, color: stampColor }}>{stampText}</span></div>
        </div>
      </div>

      <table className="w-full text-xs border-collapse mb-4">
        <thead>
          <tr style={{ background: "#0E1B2E", color: "#fff" }}>
            <th className="text-left p-2">#</th><th className="text-left p-2">Description</th><th className="text-left p-2">HSN</th>
            <th className="text-left p-2">Color/Size</th><th className="text-right p-2">Qty</th><th className="text-right p-2">Rate</th>
            <th className="text-right p-2">Disc</th><th className="text-right p-2">Amount</th>
          </tr>
        </thead>
        <tbody>
          {inv.lines.map((l, i) => (
            <tr key={i} style={{ borderBottom: "1px solid #E6E1D8" }}>
              <td className="p-2">{i + 1}</td>
              <td className="p-2 font-medium">{l.name}</td>
              <td className="p-2 font-num">{l.hsn}</td>
              <td className="p-2">{l.color || l.size ? `${l.color} / ${l.size}` : "—"}</td>
              <td className="p-2 text-right font-num">{l.qty}</td>
              <td className="p-2 text-right font-num">{inr(l.rate)}</td>
              <td className="p-2 text-right font-num">{inr(l.discount)}</td>
              <td className="p-2 text-right font-num">{inr(l.qty * l.rate - l.discount)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="flex justify-between gap-8">
        <div className="flex-1">
          <TaxBreak inv={inv} />
          <div className="text-xs"><b>Amount in words:</b> {inv.amount_in_words}</div>
          {inv.notes && <div className="text-xs mt-2"><b>Notes:</b> {inv.notes}</div>}
          {inv.status === "Returned" && inv.return_reason && <div className="text-xs mt-2 text-[#A3411D]"><b>Returned:</b> {inv.return_reason}{inv.refunded ? ` · refunded ${inr(inv.refunded)}` : ""}</div>}
          {inv.status === "Void" && inv.void_reason && <div className="text-xs mt-2 text-[#A3411D]"><b>Void:</b> {inv.void_reason}{inv.refunded ? ` · refunded ${inr(inv.refunded)}` : ""}</div>}
        </div>
        <div className="w-64 text-sm space-y-1.5">
          <div className="flex justify-between"><span className="text-[#5C626A]">Subtotal</span><span className="font-num">{inr(inv.subtotal)}</span></div>
          <div className="flex justify-between"><span className="text-[#5C626A]">Discount</span><span className="font-num">−{inr(inv.discount)}</span></div>
          <div className="flex justify-between"><span className="text-[#5C626A]">Tax</span><span className="font-num">{inr(inv.tax)}</span></div>
          <div className="flex justify-between pt-2 border-t-2 text-base font-semibold" style={{ borderColor: "#0E1B2E" }}><span>Grand Total</span><span className="font-num">{inr(inv.total)}</span></div>
          <div className="flex justify-between text-[#2D5A3C]"><span>Paid</span><span className="font-num">{inr(inv.amount_paid)}</span></div>
          <div className="flex justify-between font-semibold" style={{ color: inv.balance > 0 ? "#A3411D" : "#2D5A3C" }}><span>Balance Due</span><span className="font-num">{inr(inv.balance)}</span></div>
        </div>
      </div>

      <div className="mt-6 pt-4 border-t border-[#E6E1D8] grid grid-cols-2 gap-4 text-xs text-[#5C626A]">
        {settings.show_bank_on_invoice !== false && (settings.upi_id || settings.bank_account) ? (
          <div>
            <div className="eyebrow mb-1">Payment details</div>
            {settings.upi_id && <div>UPI: <span className="font-num">{settings.upi_id}</span></div>}
            {settings.bank_account && <div>{settings.bank_name} · A/C <span className="font-num">{settings.bank_account}</span>{settings.bank_ifsc && <> · IFSC <span className="font-num">{settings.bank_ifsc}</span></>}</div>}
          </div>
        ) : <div />}
        {settings.terms && (
          <div>
            <div className="eyebrow mb-1">Terms</div>
            <div>{settings.terms}</div>
          </div>
        )}
      </div>
      {settings.invoice_notes && <div className="text-xs text-[#5C626A] mt-3">{settings.invoice_notes}</div>}
      <div className="text-center text-xs text-[#8C929B] mt-6 font-display italic">Thank you for shopping with {settings.company_name}</div>
    </div>
  );
}

function Receipt({ inv, settings }) {
  return (
    <div className="receipt-sheet card">
      <div className="text-center mb-2">
        <div className="font-bold text-sm" style={{ fontFamily: "Playfair Display, serif" }}>{settings.company_name}</div>
        {settings.address && <div style={{ fontSize: 10 }}>{settings.address}</div>}
        {settings.phone && <div style={{ fontSize: 10 }}>{settings.phone}</div>}
        {settings.gstin && <div style={{ fontSize: 10 }}>GSTIN {settings.gstin}</div>}
      </div>
      <div style={{ borderTop: "1px dashed #999", borderBottom: "1px dashed #999", padding: "4px 0", fontSize: 10 }}>
        <div>Bill: {inv.invoice_number}</div>
        <div>{fmtDate(inv.date)} · {inv.channel}</div>
        <div>Customer: {inv.customer_name}</div>
      </div>
      <table className="w-full" style={{ fontSize: 10, margin: "6px 0" }}>
        <tbody>
          {inv.lines.map((l, i) => (
            <tr key={i}><td>{l.name} {l.size}<br />{l.qty} × {inr(l.rate)}{l.discount > 0 ? ` − ${inr(l.discount)}` : ""}</td><td className="text-right align-bottom">{inr(l.qty * l.rate - l.discount)}</td></tr>
          ))}
        </tbody>
      </table>
      <div style={{ borderTop: "1px dashed #999", paddingTop: 4, fontSize: 11 }}>
        <div className="flex justify-between"><span>Tax</span><span>{inr(inv.tax)}</span></div>
        <div className="flex justify-between font-bold"><span>TOTAL</span><span>{inr(inv.total)}</span></div>
        <div className="flex justify-between"><span>Paid</span><span>{inr(inv.amount_paid)}</span></div>
        <div className="flex justify-between"><span>Balance</span><span>{inr(inv.balance)}</span></div>
      </div>
      <div className="text-center" style={{ fontSize: 10, marginTop: 8 }}>{settings.upi_id && <>UPI {settings.upi_id}<br /></>}Thank you!</div>
    </div>
  );
}

export default function InvoiceView() {
  const { id } = useParams();
  const nav = useNavigate();
  const { isOwner } = useAuth();
  const [inv, setInv] = useState(null);
  const [settings, setSettings] = useState(null);
  const [mode, setMode] = useState("a4");
  const [payModal, setPayModal] = useState(false);
  const [pay, setPay] = useState({ amount: "", method: "UPI" });
  const [actionModal, setActionModal] = useState(null); // 'void' | 'return'
  const [reason, setReason] = useState("");
  const [refundMethod, setRefundMethod] = useState("");

  const load = useCallback(async () => {
    setInv((await api.get(`/invoices/${id}`)).data);
    setSettings((await api.get("/settings")).data);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!inv || !settings) return <div className="text-[#8C929B]">Loading invoice…</div>;
  const methods = settings.payment_methods || ["Cash", "UPI", "Card", "Bank"];
  const lastMethod = [...(inv.payments_list || [])].reverse().find((p) => p.amount > 0)?.method;

  const recordPayment = async () => {
    try {
      await api.post(`/invoices/${id}/pay`, { amount: Number(pay.amount), method: pay.method, date: pay.date });
      toast.success("Payment recorded"); setPayModal(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const doAction = async () => {
    try {
      await api.post(`/invoices/${id}/${actionModal}`, { reason, refund_method: refundMethod || null });
      const refundNote = inv.amount_paid > 0 ? ` · ${inr(inv.amount_paid)} refund recorded` : "";
      toast.success((actionModal === "void" ? "Invoice voided · stock restored" : "Return recorded · stock restored") + refundNote);
      setActionModal(null); setReason(""); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const openAction = (kind) => { setRefundMethod(lastMethod || methods[0]); setActionModal(kind); };

  const canModify = !["Void", "Returned"].includes(inv.status);

  return (
    <div>
      <div className="flex items-center justify-between mb-6 no-print">
        <button className="btn btn-ghost btn-sm" onClick={() => nav("/invoices")} data-testid="back-invoices"><ArrowLeft size={15} /> Register</button>
        <div className="flex items-center gap-2">
          <div className="flex bg-white border border-[#E6E1D8] rounded-full p-1">
            <button onClick={() => setMode("a4")} className={`px-3 py-1.5 rounded-full text-sm ${mode === "a4" ? "bg-[#0E1B2E] text-white" : "text-[#5C626A]"}`} data-testid="mode-a4">A4 Invoice</button>
            <button onClick={() => setMode("receipt")} className={`px-3 py-1.5 rounded-full text-sm ${mode === "receipt" ? "bg-[#0E1B2E] text-white" : "text-[#5C626A]"}`} data-testid="mode-receipt"><ReceiptIcon size={14} className="inline mr-1" />Receipt</button>
          </div>
          {canModify && inv.balance > 0 && inv.status !== "Draft" && <button className="btn btn-gold btn-sm" onClick={() => { setPay({ amount: inv.balance.toFixed(2), method: methods.includes("UPI") ? "UPI" : methods[0], date: todayISO() }); setPayModal(true); }} data-testid="record-payment-btn"><IndianRupee size={15} /> Record payment</button>}
          {canModify && inv.status !== "Draft" && <button className="btn btn-danger btn-sm" onClick={() => openAction("return")} data-testid="return-btn"><RotateCcw size={15} /> Return</button>}
          {isOwner && canModify && <button className="btn btn-danger btn-sm" onClick={() => openAction("void")} data-testid="void-btn"><Ban size={15} /> Void</button>}
          <button className="btn btn-primary btn-sm" onClick={() => window.print()} data-testid="print-btn"><Printer size={15} /> Print / PDF</button>
        </div>
      </div>

      <div className="print-area">
        {mode === "a4" ? <A4Invoice inv={inv} settings={settings} /> : <Receipt inv={inv} settings={settings} />}
      </div>

      {inv.payments_list?.length > 0 && (
        <div className="card mt-4 no-print max-w-[210mm] mx-auto" data-testid="payment-history">
          <div className="px-5 py-3 border-b border-[#E6E1D8] font-display text-lg">Payment history</div>
          <table className="tbl">
            <thead><tr><th>Date</th><th>Type</th><th>Method</th><th className="num">Amount</th></tr></thead>
            <tbody>
              {inv.payments_list.map((p) => (
                <tr key={p.id}>
                  <td>{fmtDate(p.date)}</td>
                  <td className="text-[#5C626A]">{p.amount < 0 ? "Refund" : "Received"}</td>
                  <td className="text-[#5C626A]">{p.method}</td>
                  <td className="num font-num" style={{ color: p.amount < 0 ? "#A3411D" : "#2D5A3C" }}>{inr(p.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={payModal} onClose={() => setPayModal(false)} title="Record Payment" testid="pay-modal">
        <div className="mb-3 text-sm">Balance due <b className="font-num">{inr(inv.balance)}</b></div>
        <label className="label">Amount</label>
        <input type="number" className="field mb-4 font-num" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} data-testid="pay-amount" />
        <div className="grid grid-cols-2 gap-3 mb-6">
          <div><label className="label">Method</label>
            <select className="field" value={pay.method} onChange={(e) => setPay({ ...pay, method: e.target.value })} data-testid="pay-method">
              {methods.map((m) => <option key={m}>{m}</option>)}
            </select></div>
          <div><label className="label">Date received</label><input type="date" className="field" value={pay.date || ""} max={todayISO()} onChange={(e) => setPay({ ...pay, date: e.target.value })} /></div>
        </div>
        <div className="flex justify-end gap-2"><button className="btn btn-ghost" onClick={() => setPayModal(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={recordPayment} disabled={!pay.amount} data-testid="pay-submit">Record</button></div>
      </Modal>

      <Modal open={!!actionModal} onClose={() => setActionModal(null)} title={actionModal === "void" ? "Void Invoice" : "Return / Credit Note"} testid="action-modal">
        <p className="text-sm text-[#5C626A] mb-4">{actionModal === "void" ? "Use void for an invoice raised by mistake. " : ""}Stock goes back to inventory and the sale is removed from revenue.</p>
        <label className="label">Reason</label>
        <input className="field mb-4" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="action-reason" />
        {inv.amount_paid > 0 && (
          <div className="mb-6">
            <label className="label">Refund {inr(inv.amount_paid)} to customer via</label>
            <select className="field" value={refundMethod} onChange={(e) => setRefundMethod(e.target.value)} data-testid="refund-method">
              {methods.map((m) => <option key={m}>{m}</option>)}
            </select>
            <p className="text-xs text-[#8C929B] mt-1">Recorded as money out in the day book.</p>
          </div>
        )}
        <div className="flex justify-end gap-2"><button className="btn btn-ghost" onClick={() => setActionModal(null)}>Cancel</button>
          <button className="btn btn-danger" onClick={doAction} disabled={!reason} data-testid="action-submit">Confirm</button></div>
      </Modal>
    </div>
  );
}
